import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { particularOrder } from "../../Services/Operations/ProductAPI";
import Header from "../Components/Header";
import { useSelector } from "react-redux";
import "../../Styles/Details.css";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { Text } from "@react-three/drei";
import { ContactShadows, Environment } from "@react-three/drei";
import { OrbitControls, Decal } from "@react-three/drei";
import { MeshStandardMaterial } from "three";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";

const Loader = () => {
  return (
    <div className="skeleton-loader">
      <div className="skeleton-header"></div>
      <div className="skeleton-card">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton-row"></div>
        ))}
      </div>
      <div className="skeleton-card">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="skeleton-row"></div>
        ))}
      </div>
      <div className="skeleton-card">
        <div className="skeleton-item"></div>
        <div className="skeleton-item"></div>
      </div>
      <style>
        {`
          .skeleton-loader {
            width: 90%;
            margin: 20px auto;
          }
          .skeleton-header {
            height: 40px;
            width: 200px;
            background: #e0e0e0;
            margin: 0 auto 20px;
            border-radius: 4px;
            animation: pulse 1.5s ease-in-out infinite;
          }
          .skeleton-card {
            background: #fff;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .skeleton-row {
            height: 20px;
            background: #f0f0f0;
            margin-bottom: 10px;
            border-radius: 4px;
            animation: pulse 1.5s ease-in-out infinite;
          }
          .skeleton-item {
            height: 100px;
            background: #f0f0f0;
            margin-bottom: 10px;
            border-radius: 4px;
            animation: pulse 1.5s ease-in-out infinite;
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </div>
  );
};

const OrderDetails = () => {
  const location = useLocation();
  const { id } = location.state || {};
  const [orderData, setOrderData] = useState(null);
  const { tokenDetails } = useSelector((state) => state.user);
  const token = tokenDetails;
  const { configUrl } = useSelector((state) => state.cart);
  console.log("id : ", id);
  const [isLoading, setIsLoading] = useState(true);

  const encodeBase64Times = (value, times = 2) => {
    let encoded = value;
    for (let i = 0; i < times; i++) {
      encoded = btoa(encoded);
    }
    return encoded;
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await particularOrder({ orderId: id }, token);
        console.log("Particular data : ", response);
        if (response.success) {
          setOrderData(response.data[0]);
        } else {
          console.error("Failed to fetch order details");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchOrderDetails();
    }
  }, [id]);

  if (isLoading) {
    return <Loader />;
  }

  const formattedDate = new Date(orderData.created_at).toLocaleDateString();
  const formattedTime = new Date(orderData.created_at).toLocaleTimeString();

  const GlbViewer = ({ customizeFile, settings }) => {
    const gltf = useLoader(GLTFLoader, `${configUrl}${customizeFile}`);
    const { nodes, materials } = useLoader(
      GLTFLoader,
      `${configUrl}${customizeFile}`
    );
    const groupRef = useRef();
    const [parts, setParts] = useState([]);

    const frontTextTextureRef = useRef();
    const backTextTextureRef = useRef();
    const frontNumberTextureRef = useRef();
    const backNumberTextureRef = useRef();

    const logoTexture = useMemo(() => {
      console.log("texture frontLogoBase64 : ", settings.frontLogo);
      const texture = new THREE.TextureLoader().load(settings.frontLogo);
      texture.needsUpdate = true;
      return texture;
    }, [settings.frontLogo]);

    const logoTextureBack = useMemo(() => {
      const texture = new THREE.TextureLoader().load(settings.backLogo);
      texture.needsUpdate = true;
      return texture;
    }, [settings.backLogo]);

    useEffect(() => {
      const createCanvasTexture = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 512;
        const texture = new THREE.CanvasTexture(canvas);
        return { canvas, texture };
      };

      frontTextTextureRef.current = createCanvasTexture().texture;
      backTextTextureRef.current = createCanvasTexture().texture;
      frontNumberTextureRef.current = createCanvasTexture().texture;
      backNumberTextureRef.current = createCanvasTexture().texture;
    }, []);

    const updateTextTexture = (textureRef, text, partName, Colors) => {
      if (!text || !textureRef.current) return;

      const canvas = textureRef.current.image;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        console.error("Canvas context not found.");
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const colorMapping = {
        frontText: "front",
        backText: "back",
        frontNumber: "frontNumber",
        backNumber: "backNumber",
      };

      const colorKey = colorMapping[partName] || partName;
      ctx.fillStyle = Colors?.[colorKey] || "black";

      ctx.font = "bold 80px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);

      textureRef.current.needsUpdate = true;
    };

    useEffect(() => {
      updateTextTexture(
        frontTextTextureRef,
        settings.frontText,
        "frontText",
        settings.Colors
      );
      updateTextTexture(
        backTextTextureRef,
        settings.backText,
        "backText",
        settings.Colors
      );
      updateTextTexture(
        frontNumberTextureRef,
        settings.frontNumber,
        "frontNumber",
        settings.Colors
      );
      updateTextTexture(
        backNumberTextureRef,
        settings.backNumber,
        "backNumber",
        settings.Colors
      );
    }, [
      settings.frontText,
      settings.backText,
      settings.frontNumber,
      settings.backNumber,
      settings.Colors,
    ]);

    useEffect(() => {
      if (gltf?.scene) {
        const extractedParts = [];
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            extractedParts.push({
              name: child.name,
              geometry: child.geometry,
              material:
                child.material ||
                new THREE.MeshStandardMaterial({ color: "red" }),
            });

            if (child.material) {
              child.material.side = THREE.DoubleSide;
              child.material.needsUpdate = true;
            }
          }
        });
        setParts(extractedParts);
      }
    }, [gltf]);

    useEffect(() => {
      parts.forEach((part) => {
        if (part.material.map) {
          part.material.map.needsUpdate = true;
        }
        part.material.needsUpdate = true;
      });
    }, [parts]);

    useFrame(() => {
      if (groupRef.current) {
        groupRef.current.position.set(...settings.position);
        groupRef.current.rotation.set(...settings.rotation);
        groupRef.current.scale.set(
          settings.scale,
          settings.scale,
          settings.scale
        );
      }
    });

    return (
      <>
        <OrbitControls
          enableZoom
          enablePan
          target={[0, 0, 0]}
          enableRotate
          rotateSpeed={3.0}
        />
        <group dispose={null} ref={groupRef}>
          {parts.map((part, index) => (
            <mesh
              key={index}
              castShadow
              receiveShadow
              geometry={part.geometry}
              material={part.material}
            >
              <Decal
                position={settings.frontTextPosition}
                rotation={settings.frontTextRotation}
                scale={settings.frontTextScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={frontTextTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.frontNumberPosition}
                rotation={settings.frontNumberRotation}
                scale={settings.frontNumberScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={frontNumberTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.backTextPosition}
                rotation={settings.backTextRotation}
                scale={settings.backTextScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={backTextTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.backNumberPosition}
                rotation={settings.backNumberRotation}
                scale={settings.backNumberScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={backNumberTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.frontLogoPosition}
                rotation={settings.frontLogoRotation}
                scale={settings.frontLogoScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial map={logoTexture} transparent />
              </Decal>

              <Decal
                position={settings.backLogoPosition}
                rotation={settings.backLogoRotation}
                scale={settings.backLogoScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial map={logoTextureBack} transparent />
              </Decal>
            </mesh>
          ))}
        </group>
      </>
    );
  };

  return (
    <div className="order-details-container">
      
      <div style={styles.container}>
        <h2 style={styles.header} className="learge1">
          Order Details
        </h2>

        <div style={styles.orderCard}>
          <div style={styles.orderSection}>
            <h3 style={styles.sectionHeader}>Order Summary</h3>
            <div style={styles.row}>
              <span style={styles.label}>Order ID:</span>
              <span style={styles.value}>{orderData.id}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Date:</span>
              <span style={styles.value}>{formattedDate}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Time:</span>
              <span style={styles.value}>{formattedTime}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Order Type:</span>
              <span style={styles.value}>{orderData.order_type}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Order Status:</span>
              <span
                style={{
                  ...styles.value,
                  color:
                    orderData.order_status === "Pending"
                      ? "#ff4d4f"
                      : "#28a745",
                }}
              >
                {orderData.order_status}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Payment Status:</span>
              <span
                style={{
                  ...styles.value,
                  color:
                    orderData.payment_status === "Pending"
                      ? "#ff4d4f"
                      : "#28a745",
                }}
              >
                {orderData.payment_status}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Coupon Code:</span>
              <span style={styles.value}>{orderData.coupon_code || "N/A"}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Total Discount:</span>
              <span style={styles.value}>
                ${orderData.total_discount.toFixed(2)}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Total Amount:</span>
              <span style={styles.value}>
                ${orderData.total_amount.toFixed(2)}
              </span>
            </div>
          </div>

          <div style={styles.row}>
            <span style={styles.label}>Tracking ID:</span>
            <span style={styles.value}>{orderData.tracking_id || "N/A"}</span>
          </div>

          {orderData.tracking_url && (
            <div style={{ textAlign: "center", marginTop: "12px" }}>
              <a
                href={orderData.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "#fff",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: "bold",
                  transition: "background-color 0.3s",
                  marginBottom: "20px",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#0056b3")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "#007bff")
                }
              >
                Track Your Order
              </a>
            </div>
          )}

          <div style={styles.orderSection}>
            <h3 style={styles.sectionHeader}>Delivery Address</h3>
            <div style={styles.row}>
              <span style={styles.label}>Name:</span>
              <span style={styles.value}>{orderData.address.name}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Mobile:</span>
              <span style={styles.value}>{orderData.address.mobile_no}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Address:</span>
              <span style={styles.value}>{orderData.address.address}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>City:</span>
              <span style={styles.value}>{orderData.address.city}</span>
            </div>
          </div>

          <div style={styles.orderSection}>
            <h3 style={styles.sectionHeader}>Items</h3>
            <h5>Product</h5>
            {orderData?.items?.product?.map((item, index) => (
              <div key={index} style={styles.itemCard}>
                <img
                  src={`${item?.product?.image}`}
                  alt={item?.product?.name}
                  className="product-image"
                />
                <div style={styles.itemDetails}>
                  <div style={styles.row}>
                    <span style={styles.label}>Product:</span>
                    <span style={styles.value}>{item?.product?.name}</span>
                  </div>
                  <div style={styles.row}>
                    <span style={styles.label}>Name:</span>
                    <span style={styles.value}>{item?.name}</span>
                  </div>
                  <div style={styles.row}>
                    <span style={styles.label}>Number:</span>
                    <span style={styles.value}>{item?.number}</span>
                  </div>
                  <div style={styles.row}>
                    <span style={styles.label}>Size:</span>
                    <span style={styles.value}>{item?.size?.name}</span>
                  </div>
                  <div style={styles.row}>
                    <span style={styles.label}>Quantity:</span>
                    <span style={styles.value}>{item?.quantity}</span>
                  </div>
                  <div style={styles.row}>
                    <span style={styles.label}>Price:</span>
                    <span style={styles.value}>${item?.amount.toFixed(2)}</span>
                  </div>
                  <div style={styles.row}>
                    <span style={styles.label}>Total:</span>
                    <span style={styles.value}>
                      ${item?.total_amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <h5>Glb</h5>
            {orderData?.items?.glb.map((item, index) => {
              console.log("item.glb_id : ", item);
              const encodedGlbId = encodeBase64Times(item.id);
              return (
                <>
                  <div key={index} style={styles.itemCard}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={`${configUrl}${item.glb.customize_image}`}
                        alt={item.glb.name}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                        }}
                      />
                      <Link
                        to={`/customizer?glbId=${encodedGlbId}`}
                        className="view-3d-link"
                        style={{ color: "#d9ff16" }}
                      >
                        View in 3D
                      </Link>
                    </div>
                    <div style={styles.itemDetails}>
                      <div style={styles.row}>
                        <span style={styles.label}>Product:</span>
                        <span style={styles.value}>{item?.glb?.name}</span>
                      </div>
                      <div style={styles.row}>
                        <span style={styles.label}>Size:</span>
                        <span style={styles.value}>{item?.size?.name}</span>
                      </div>
                      <div style={styles.row}>
                        <span style={styles.label}>Quantity:</span>
                        <span style={styles.value}>{item?.quantity}</span>
                      </div>
                      <div style={styles.row}>
                        <span style={styles.label}>Price:</span>
                        <span style={styles.value}>
                          ${item?.amount.toFixed(2)}
                        </span>
                      </div>
                      <div style={styles.row}>
                        <span style={styles.label}>Total:</span>
                        <span style={styles.value}>
                          ${item?.total_amount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: "90%",
    margin: "20px auto",
    textAlign: "center",
  },
  header: {
    fontWeight: "bold",
    color: "#333",
    marginBottom: "20px",
    textAlign: "center",
  },
  orderCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "20px",
    textAlign: "left",
  },
  orderSection: {
    marginBottom: "20px",
  },
  sectionHeader: {
    fontSize: "21px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#ffffff",
    backgroundColor: "black",
    padding: "10px 20px",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
    marginBottom: "20px",
    textTransform: "uppercase",
    letterSpacing: "2px",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontSize: "16px",
    flexWrap: "wrap",
  },
  label: {
    fontWeight: "bold",
    color: "#555",
    flexBasis: "40%",
    textAlign: "left",
  },
  value: {
    color: "#333",
    flexBasis: "55%",
    textAlign: "right",
  },
  itemCard: {
    display: "flex",
    flexDirection: "row",
    gap: "10px",
    padding: "10px",
    border: "1px solid #eee",
    borderRadius: "8px",
    marginBottom: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  itemDetails: {
    flex: 1,
    minWidth: "180px",
  },
  "@media (max-width: 768px)": {
    container: {
      width: "95%",
    },
    row: {
      fontSize: "14px",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    label: {
      flexBasis: "100%",
      textAlign: "left",
    },
    value: {
      flexBasis: "100%",
      textAlign: "left",
    },
    itemCard: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
    sectionHeader: {
      fontSize: "18px",
      padding: "8px 16px",
    },
  },
};

export default OrderDetails;
